import React, { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiAlertTriangle, FiCheckCircle, FiLayers, FiTool } from "react-icons/fi";

const API_REGISTRY_HEALTH = `${getHost()}/api/admin/runtime-validation`;
const getToken = () => localStorage.getItem("jwt");

const SummaryCard = ({ icon, label, value, color }) => (
  <Col lg={4} md={6} xs={6} className="mb-3">
    <Card className="border border-dark bg-transparent h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          {icon}
          <span className="text-white-65 fs-12">{label}</span>
        </div>
        <h4 className="fw-600 mb-0" style={{ color: color || "#ADADAD" }}>
          {value}
        </h4>
      </Card.Body>
    </Card>
  </Col>
);

const IssueList = ({ title, items, emptyLabel, renderItem }) => (
  <Card className="border border-dark bg-transparent h-100">
    <Card.Body className="p-3">
      <div className="text-main fw-600 fs-14 mb-3">{title}</div>
      {items.length === 0 ? (
        <div className="text-white-65 fs-13">{emptyLabel}</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {items.map((item) => (
            <div
              key={typeof item === "string" ? item : item.code_key || item.folder_name}
              className="rounded-3 p-2"
              style={{ background: "#141414", border: "1px solid #222" }}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </Card.Body>
  </Card>
);

const AdminRuntimeValidation = () => {
  const { toggleSidebar } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    fetch(API_REGISTRY_HEALTH, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => setHealth(data))
      .catch(() => setHealth({ available: false, issue_count: 0, unknown_code_keys: [], duplicate_folder_names: [], unused_code_keys: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiTool className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Runtime Validation</h4>
              <p className="text-white-65 fs-14 mb-0">
                Validate live monitor DB keys against the runtime registry.
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 pb-4">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="light" />
          </div>
        ) : (
          <>
            <Row>
              <SummaryCard
                icon={<FiAlertTriangle className="text-danger fs-18" />}
                label="Issues"
                value={health?.issue_count ?? 0}
                color={(health?.issue_count ?? 0) > 0 ? "#FF4444" : "#00E98E"}
              />
              <SummaryCard
                icon={<FiLayers className="text-info fs-18" />}
                label="Mongo Monitors"
                value={health?.monitor_count ?? 0}
              />
              <SummaryCard
                icon={<FiCheckCircle className="text-success fs-18" />}
                label="Registry Keys"
                value={health?.registry_size ?? 0}
              />
            </Row>

            {!health?.available && (
              <Card className="border border-dark bg-transparent mb-3">
                <Card.Body className="p-3 text-white-65 fs-13">
                  Runtime validation data is not available yet. The `discord_bot` publisher may not be deployed on the latest code.
                </Card.Body>
              </Card>
            )}

            <Row>
              <Col lg={6} className="mb-3">
                <IssueList
                  title="Missing Runtime Class"
                  items={health?.unknown_code_keys || []}
                  emptyLabel="No missing runtime keys."
                  renderItem={(item) => (
                    <>
                      <div className="text-danger fw-600 fs-13">{item.code_key || item.folder_name}</div>
                      <div className="text-white-65 fs-12">{item.names.join(", ")}</div>
                    </>
                  )}
                />
              </Col>
              <Col lg={6} className="mb-3">
                <IssueList
                  title="Duplicate Folder Names"
                  items={health?.duplicate_folder_names || []}
                  emptyLabel="No duplicate folder names."
                  renderItem={(item) => (
                    <>
                      <div className="text-warning fw-600 fs-13">{item.folder_name}</div>
                      <div className="text-white-65 fs-12">{item.names.join(", ")}</div>
                    </>
                  )}
                />
              </Col>
            </Row>

            <Row>
              <Col lg={12} className="mb-3">
                <IssueList
                  title="Unused Registry Keys"
                  items={health?.unused_code_keys || []}
                  emptyLabel="No unused runtime keys."
                  renderItem={(item) => (
                    <div className="text-white-65 fs-13">{item}</div>
                  )}
                />
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminRuntimeValidation;
